import React, { MouseEvent, useEffect, useState } from 'react';
import { Button, InputAdornment, styled, TextField, Typography } from '@mui/material';
import { MedicalServicesOutlined, PaidOutlined } from '@mui/icons-material';
import Dialog from 'components/dialogs/Dialog';
import DoctorsAutoComplete from 'components/autocomplete/DoctorsAutoComplete';
import MoneyInput from 'components/masked-input/MoneyInput';
import { toast } from 'react-toastify';
import { Doctor } from 'types/doctor';
import { ProcedureDoctor } from 'types/procedure';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface ProcedureDoctorFormProps {
  onExited: () => void;
  handleAddDoctor: (doctorInfo: ProcedureDoctor | null) => void;
  doctorInfo: ProcedureDoctor | null;
  handleChangeDoctorInfo<K extends keyof ProcedureDoctor>(index: K, value: ProcedureDoctor[K]): void;
  type: 'new' | 'edit';
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
});

const Intro = styled('div')({
  border: '1px solid #eadde1',
  borderRadius: 8,
  padding: 12,
  background: 'linear-gradient(135deg, #fff7fa 0%, #fff 100%)',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
});

const IconBox = styled('div')({
  width: 34,
  height: 34,
  borderRadius: 8,
  background: '#fff4f7',
  color: '#7b1235',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
});

const PriceGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const DialogActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  paddingTop: 4,
});

const ProcedureDoctorForm: React.FC<ProcedureDoctorFormProps> = ({
  handleAddDoctor,
  onExited,
  doctorInfo,
  handleChangeDoctorInfo,
  type,
}) => {
  const [doctor, setDoctor] = useState<Doctor | null>(null);

  useEffect(() => {
    setDoctor(doctorInfo?.doctor || null);
  }, [doctorInfo]);

  function handleValidation(e?: MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    if (!doctor || !doctorInfo?.doctor_id) {
      toast.error('Selecione um médico');
      return;
    }

    if (!doctorInfo.sale_price_min) {
      toast.error('Informe o preço de venda mínimo');
      return;
    }

    if (!doctorInfo.sale_price_max) {
      toast.error('Informe o preço de venda máximo');
      return;
    }

    handleAddDoctor({
      ...doctorInfo,
      doctor: doctor,
      doctor_id: doctor.id,
    });
  }

  return (
    <Dialog
      onExited={onExited}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      title={type === 'edit' ? 'Editar médico' : 'Adicionar médico'}
    >
      <Container>
        <Intro>
          <IconBox>
            <MedicalServicesOutlined fontSize="small" />
          </IconBox>
          <div>
            <Typography variant="body2" fontWeight={800}>
              Médico e faixa de preço
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Selecione o profissional e informe os valores usados no orçamento.
            </Typography>
          </div>
        </Intro>

        <DoctorsAutoComplete
          label="Médico"
          onSelect={doctor => {
            handleChangeDoctorInfo('doctor_id', doctor?.id || 0);
            handleChangeDoctorInfo('doctor', doctor);
            setDoctor(doctor);
          }}
          placeholder="Selecione um médico"
          selected={doctor}
        />

        <PriceGrid>
          <TextField
            label="Preço mínimo"
            placeholder="Digite o preço mínimo"
            value={doctorInfo?.sale_price_min || 0}
            onChange={e => handleChangeDoctorInfo('sale_price_min', Number(e.target.value))}
            fullWidth
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <PaidOutlined fontSize="small" />
                  </InputAdornment>
                ),
                inputComponent: asInputComponent(MoneyInput),
              },
            }}
          />

          <TextField
            label="Preço máximo"
            placeholder="Digite o preço máximo"
            value={doctorInfo?.sale_price_max || 0}
            onChange={e => handleChangeDoctorInfo('sale_price_max', Number(e.target.value))}
            fullWidth
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <PaidOutlined fontSize="small" />
                  </InputAdornment>
                ),
                inputComponent: asInputComponent(MoneyInput),
              },
            }}
          />
        </PriceGrid>

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onExited}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleValidation}>
            {type === 'new' ? 'Adicionar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Container>
    </Dialog>
  );
};

export default ProcedureDoctorForm;
